/**
 * Procurement type of a proposal / work (server migration 217).
 *
 * Decides the wording a screen uses and whether bill lines can become assets. The
 * server is the authority (utils/procurement-type.utils.js); this only mirrors the
 * three codes and their labels.
 */

export type ProcurementType = 'WORKS' | 'GOODS' | 'SERVICES';

export const PROCUREMENT_TYPE_OPTIONS: Array<{
  code: ProcurementType;
  label: string;
  hint: string;
  icon: string;
}> = [
  { code: 'WORKS', label: 'Works', hint: 'Construction or repair on site: roads, drains, buildings', icon: 'ri-building-2-line' },
  { code: 'GOODS', label: 'Goods', hint: 'Buying ready-made items: computers, furniture, vehicles', icon: 'ri-shopping-cart-2-line' },
  { code: 'SERVICES', label: 'Services', hint: 'Work done by people: security, AMC, consultancy', icon: 'ri-customer-service-2-line' },
];

export const normaliseProcurementType = (value: unknown): ProcurementType => {
  const code = String(value ?? '').trim().toUpperCase();
  return code === 'GOODS' || code === 'SERVICES' ? code : 'WORKS';
};

export const procurementTypeLabel = (value: unknown): string =>
  PROCUREMENT_TYPE_OPTIONS.find((o) => o.code === normaliseProcurementType(value))?.label ?? 'Works';

/** Screen wording that differs between a construction work and a purchase. */
export const procurementWording = (value: unknown) => {
  const type = normaliseProcurementType(value);
  if (type === 'GOODS') {
    return {
      contract: 'Purchase Order',
      contractor: 'Supplier',
      measurement: 'Goods Receipt & Inspection',
      measuredQty: 'Quantity received',
    };
  }
  if (type === 'SERVICES') {
    return {
      contract: 'Service Order',
      contractor: 'Service Provider',
      measurement: 'Service Completion Record',
      measuredQty: 'Quantity completed',
    };
  }
  return {
    contract: 'Contract',
    contractor: 'Contractor',
    measurement: 'Measurement Book',
    measuredQty: 'Measured quantity',
  };
};
